import type { FC } from "react";

import dayjs from "dayjs";
import _ from "lodash";
import numeral from "numeral";
import { compose } from "redux";
import styled from "styled-components";

import {
    generateColumnChartData,
    generateLineChartData,
    generateStackedColumnChartData,
} from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartDataGenerator";
import type {
    IPoPGraphProps,
    ISeries,
} from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartTypes";

import { FlexRow } from "../../../../../styled components/StyledFlex/src/StyledFlex";
import { ComparePeriodsLegend } from "../../../../Legends/src/ComparePeriodsLegend/ComparePeriodsLegend";
import Chart from "../../Chart";

import chartConfig from "./chartConfig";
import { ChartContainer, ChartLegendContainer } from "./StyledComponents";

const LegendRow = styled(FlexRow)`
    justify-content: space-between;
`;

const defaultXAxisFormatter = ({ value }: { value: FixmeType }) =>
    value ? dayjs.utc(value).utc().format("MMM") : "N/A";

const categoryXAxisFormatter = ({ value }: { value: FixmeType }) => value;

const stackedColumnXFormatter = (chartDataSeries: ISeries[]) => {
    const durationDiff = dayjs
        .utc(chartDataSeries[0].Values[0].Key)
        .diff(dayjs.utc(chartDataSeries[0].Values[1].Key), "months");
    return ({ value }: { value: FixmeType }) =>
        value
            ? `${dayjs.utc(value).utc().subtract(durationDiff, "months").format("MMM YYYY")} - ${dayjs
                  .utc(value)
                  .utc()
                  .format("MMM YYYY")}`
            : "N/A";
};

const resolveXAxisFormatter = (
    type: string,
    chartDataSeries: ISeries[],
    xAxisFormatter: (({ value }: { value: FixmeType }) => string) | undefined,
    isStackedColumn: boolean,
    isCategoryXSeries?: boolean,
) => {
    if (type === "column" && isStackedColumn) {
        return stackedColumnXFormatter(chartDataSeries);
    }

    return xAxisFormatter || isCategoryXSeries ? categoryXAxisFormatter : defaultXAxisFormatter;
};

const generateChartData = (
    type: string,
    chartTitles: string[],
    chartDataSeries1: ISeries[],
    chartDataSeries2: ISeries[],
    isStackedColumn: boolean,
) => {
    if (type === "column") {
        return isStackedColumn
            ? generateStackedColumnChartData(chartTitles, chartDataSeries1, chartDataSeries2)
            : generateColumnChartData(chartTitles, chartDataSeries1);
    }

    return generateLineChartData(chartTitles, chartDataSeries1);
};
export const PeriodOverPeriodChart: FC<IPoPGraphProps> = ({
    type = "line",
    data,
    xAxisFormatter,
    yAxisFormatter = ({ value }: { value: FixmeType }) =>
        _.isNumber(value) ? numeral(value).format("0[.]0a").toUpperCase() : "N/A",
    legendDurations,
    options = { stackedColumn: false, categoryXSeries: false },
    showLegend = true,
    postProcessChartData = _.identity,
    postProcessChartConfig = _.identity,
    rightSectionComponent,
    metric,
    timeGranularity,
}) => {
    const titles = Object.keys(data ?? {});
    const series1: ISeries[] = (data as FixmeType)?.[titles[0]] ?? [];
    const series2: ISeries[] = (data as FixmeType)?.[titles[1]] ?? [];
    const isStackedColumn = type === "column" && !!options.stackedColumn && titles.length > 1;
    const legendTitles = isStackedColumn ? [titles[1], titles[0], titles[1], titles[0]] : [titles[0], titles[0]];

    const selectedXAxisFormatter = resolveXAxisFormatter(
        type,
        series1,
        xAxisFormatter,
        isStackedColumn,
        options?.categoryXSeries ?? false,
    );
    const chartData = compose(postProcessChartData, generateChartData)(type, titles, series1, series2, isStackedColumn);

    const config = compose(
        postProcessChartConfig,
        chartConfig,
    )({
        type,
        xAxisFormatter: selectedXAxisFormatter,
        yAxisFormatter,
        isStackedColumn,
        categoryXSeries: options.categoryXSeries,
        legendDurations,
        height: options.height,
        metric,
        timeGranularity,
    });

    return (
        <ChartLegendContainer>
            <LegendRow>
                {legendDurations && showLegend ? (
                    <ComparePeriodsLegend
                        titles={legendTitles}
                        durations={legendDurations}
                    />
                ) : null}
                {rightSectionComponent}
            </LegendRow>
            <ChartContainer className="chartContainer">
                <Chart
                    type={type}
                    data={chartData}
                    config={config}
                />
            </ChartContainer>
        </ChartLegendContainer>
    );
};

PeriodOverPeriodChart.displayName = "PeriodOverPeriodGraph";
